import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlreadyClaimedError,
  AuthExpiredError,
  ImNotFoundError,
  NotAllowedError,
  addDealLink,
  addDealNote,
  dealFileHref,
  dealFileThumbnailHref,
  imThumbnailHref,
  deleteDealFile,
  listDealFiles,
  uploadDealFiles,
  DEAL_FILE_ACCEPT,
  DEAL_FILE_CATEGORIES,
  DEAL_FILE_CATEGORY_LABELS,
  type DealFile,
  type DealFileCategory,
  assignIm,
  claimIm,
  releaseIm,
  deleteDealNote,
  fetchImNarration,
  getIm,
  imDownloadHref,
  imPreviewHref,
  teaserPreviewHref,
  listDealEmails,
  listDealNotes,
  patchIm,
  STAGES,
  REJECTION_STAGES,
  STAGE_LABELS,
  type DealEmail,
  type DealNote,
  type Im,
  type PatchImFields,
  type Stage,
} from '../api/ims.js';
import { Avatar, OwnerBadge, useSession } from '../components/Session.js';
import { datetime, money, plain } from '../format.js';

/**
 * Whether the signed-in person may change this deal (its owner, or an admin).
 * Everyone can read every deal; for anyone else the fields render as plain
 * text and the buttons are off. The server enforces the same rule.
 */
const EditableContext = createContext(true);

type LoadState = 'loading' | 'ready' | 'error' | 'notfound';

// EBITDA margin, when both numbers are present.
function margin(revenue: string | null, ebitda: string | null): string {
  const r = Number(revenue);
  const e = Number(ebitda);
  if (!revenue || !ebitda || !Number.isFinite(r) || !Number.isFinite(e) || r === 0) return '—';
  return `${Math.round((e / r) * 100)}%`;
}

// Shape the rate-ims agent stores in rating_detail (see server rate-ims/run.ts).
interface RatingDetail {
  dimensions?: { name: string; score: number; rationale?: string }[];
  overallScore?: number;
  verdict?: string;
  notes?: string;
  productSummary?: string;
}

// One labelled, click-to-edit field. Text or number; commits on Enter/blur,
// cancels on Escape. `multiline` renders a textarea (used for Notes).
function EditField({
  label,
  raw,
  display,
  kind,
  onSave,
  saving,
  multiline = false,
}: {
  label: string;
  raw: string | null;
  display: React.ReactNode;
  kind: 'text' | 'number';
  onSave: (next: string | number | null) => void;
  saving: boolean;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const doneRef = useRef(false);
  const editable = useContext(EditableContext);

  const begin = () => {
    doneRef.current = false;
    setDraft(raw ?? '');
    setEditing(true);
  };
  const cancel = () => {
    doneRef.current = true;
    setEditing(false);
  };
  const commit = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setEditing(false);
    const trimmed = draft.trim();
    if (kind === 'number') {
      const next = trimmed === '' ? null : Number(trimmed);
      if (next !== null && Number.isNaN(next)) return;
      const cur = raw === null || raw === '' ? null : Number(raw);
      if (next === cur) return;
      onSave(next);
    } else {
      const next = trimmed === '' ? null : trimmed;
      if ((next ?? '') === (raw ?? '')) return;
      onSave(next);
    }
  };

  return (
    <div className="flex flex-col gap-1 py-2">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</span>
      {editing ? (
        multiline ? (
          <textarea
            autoFocus
            rows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
              }
            }}
            className="rounded border border-positive-500/50 bg-ink-900 px-2 py-1 text-sm outline-none"
          />
        ) : (
          <input
            autoFocus
            type={kind === 'number' ? 'number' : 'text'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
              }
            }}
            className="w-full rounded border border-positive-500/50 bg-ink-900 px-2 py-1 text-sm outline-none"
          />
        )
      ) : !editable ? (
        <div className={`py-0.5 text-sm text-ink-100 ${multiline ? 'whitespace-pre-wrap' : ''}`}>
          {display}
        </div>
      ) : (
        <button
          type="button"
          onClick={begin}
          title="Click to edit"
          className={`-mx-1 rounded px-1 py-0.5 text-left text-sm text-ink-100 hover:bg-white/5 ${
            saving ? 'opacity-50' : ''
          } ${multiline ? 'whitespace-pre-wrap' : ''}`}
        >
          {display}
        </button>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  value,
  onToggle,
  saving,
  offTone = 'neutral',
}: {
  label: string;
  value: boolean;
  onToggle: (next: boolean) => void;
  saving: boolean;
  offTone?: 'neutral' | 'negative';
}) {
  const editable = useContext(EditableContext);
  const onCls = 'bg-positive-500/15 text-positive-500';
  const offCls =
    offTone === 'negative' ? 'bg-negative-500/15 text-negative-500' : 'bg-white/5 text-ink-300';
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</span>
      <button
        type="button"
        disabled={saving || !editable}
        aria-pressed={value}
        onClick={() => onToggle(!value)}
        className={`pill ${value ? onCls : offCls} disabled:opacity-50`}
      >
        {value ? 'Yes' : 'No'}
      </button>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-semibold text-ink-100">{title}</h2>
      {children}
    </div>
  );
}

// The audio-briefing ("Listen") lifecycle. Generation takes ~20s, so by the
// time the URL arrives the user's click gesture has long expired and the
// browser may refuse a programmatic play() (autoplay policy) — the old design
// awaited play() on a detached Audio object and could hang on "Preparing
// audio…" forever. Instead we render a real <audio controls> element once the
// URL is ready: autoplay is merely attempted, and if the browser blocks it the
// native controls are right there — a direct press on them always works.
// Edits to the deal reset the state so a replay regenerates.
type NarrationState = 'idle' | 'loading' | 'ready';

/**
 * The correspondence behind a deal: what the agent read in the mailbox and
 * what it sent as Andi. Outbound messages the agent wrote are marked, because
 * the one thing you always want to know about an autonomous emailer is which
 * of these words were yours.
 */
function Correspondence({ imId }: { imId: string }) {
  const [emails, setEmails] = useState<DealEmail[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let live = true;
    listDealEmails(imId)
      .then((rows) => {
        if (live) setEmails(rows);
      })
      .catch((e) => {
        if (live) setError((e as Error).message);
      });
    return () => {
      live = false;
    };
  }, [imId]);

  if (error) return <p className="text-sm text-negative-500">{error}</p>;
  if (emails === null) return <p className="text-sm text-ink-300">Loading…</p>;
  if (emails.length === 0) {
    return <p className="text-sm text-ink-500">No correspondence recorded for this deal yet.</p>;
  }

  return (
    <ol className="space-y-2">
      {emails.map((email) => {
        const open = expanded.has(email.id);
        const outbound = email.direction === 'outbound';
        return (
          <li key={email.id} className="rounded-lg border border-white/5 bg-ink-900/40 p-3">
            <button
              type="button"
              onClick={() =>
                setExpanded((prev) => {
                  const next = new Set(prev);
                  if (next.has(email.id)) next.delete(email.id);
                  else next.add(email.id);
                  return next;
                })
              }
              className="flex w-full items-start justify-between gap-3 text-left"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-ink-100">
                  {email.subject ?? '(no subject)'}
                </span>
                <span className="mt-0.5 block truncate text-xs text-ink-300">
                  {outbound ? `To ${email.to_addrs ?? '?'}` : `From ${email.from_addr ?? '?'}`}
                  {email.sent_at ? ` · ${datetime(email.sent_at)}` : ''}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                {email.by_agent ? (
                  <span className="pill bg-warn-500/15 text-warn-500">Sent by agent</span>
                ) : null}
                {email.kind ? <span className="pill bg-white/5 text-ink-300">{email.kind}</span> : null}
                <span className="pill bg-white/5 text-ink-300">{outbound ? 'Out' : 'In'}</span>
              </span>
            </button>
            {open && email.body_text ? (
              <pre className="mt-3 whitespace-pre-wrap border-t border-white/5 pt-3 font-sans text-sm text-ink-100">
                {email.body_text}
              </pre>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The running commentary on a deal, as a team chat: oldest at the top, each
 * message with the face of whoever left it (Claude included) and when.
 * Appended and never rewritten. Distinct from the single "Summary note" field
 * above, which says what is true now — this says what was true in June.
 *
 * The agent reads the most recent of these before it writes anything, so a
 * note like "they're away until October" changes what it does.
 */
function Notes({ imId }: { imId: string }) {
  const editable = useContext(EditableContext);
  const { user, isAdmin, ownerName } = useSession();
  const me = user.email.toLowerCase();
  // Oldest first, the way a conversation reads (the API returns newest first).
  const [notes, setNotes] = useState<DealNote[] | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scroller = useRef<HTMLOListElement>(null);

  useEffect(() => {
    let live = true;
    listDealNotes(imId)
      .then((rows) => {
        if (live) setNotes([...rows].reverse());
      })
      .catch((e) => {
        if (live) setError((e as Error).message);
      });
    return () => {
      live = false;
    };
  }, [imId]);

  // Keep the latest message in view, on load and after each new one.
  const count = notes?.length ?? 0;
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count]);

  const submit = async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    setError(null);
    try {
      const note = await addDealNote(imId, body);
      setNotes((prev) => [...(prev ?? []), note]);
      setDraft('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (noteId: string) => {
    const previous = notes;
    setNotes((prev) => (prev ?? []).filter((n) => n.id !== noteId));
    try {
      await deleteDealNote(imId, noteId);
    } catch (e) {
      setNotes(previous);
      setError((e as Error).message);
    }
  };

  return (
    <div className="space-y-3">
      {notes === null ? (
        <p className="text-sm text-ink-300">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-ink-500">No messages yet.</p>
      ) : (
        <ol ref={scroller} className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
          {notes.map((note) => {
            const mine = note.author === me;
            return (
              <li key={note.id} className={`group flex gap-2.5 ${mine ? 'flex-row-reverse' : ''}`}>
                <Avatar author={note.author} />
                <div className={`flex min-w-0 max-w-[85%] flex-col ${mine ? 'items-end' : 'items-start'}`}>
                  <div className={`flex items-baseline gap-2 ${mine ? 'flex-row-reverse' : ''}`}>
                    <span className="text-xs font-medium text-ink-100">
                      {ownerName(note.author) ?? 'Unknown'}
                    </span>
                    <time className="text-[11px] tabular-nums text-ink-500" dateTime={note.created_at}>
                      {datetime(note.created_at)}
                    </time>
                    {isAdmin || mine ? (
                      <button
                        type="button"
                        onClick={() => void remove(note.id)}
                        className="text-[11px] text-ink-500 opacity-0 transition-opacity hover:text-negative-500 focus:opacity-100 group-hover:opacity-100"
                        aria-label="Delete this message"
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                  <p
                    className={`mt-1 whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm text-ink-100 ${
                      mine ? 'rounded-tr-sm bg-sky-500/20' : 'rounded-tl-sm bg-ink-900/60'
                    }`}
                  >
                    {note.body}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {editable ? (
        <div className="border-t border-white/5 pt-3">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends, as in any chat; Shift+Enter starts a new line.
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void submit();
                }
              }}
              rows={2}
              placeholder="Write a message…"
              className="min-w-0 flex-1 resize-y rounded-lg border border-white/5 bg-ink-900/60 px-3 py-2 text-sm text-ink-100 placeholder:text-ink-500 focus:border-white/20 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => void submit()}
              disabled={saving || draft.trim() === ''}
              className="rounded-md bg-white/10 px-3 py-2 text-xs font-medium text-ink-100 hover:bg-white/20 disabled:opacity-40"
            >
              {saving ? 'Sending…' : 'Send'}
            </button>
          </div>
          <div className="mt-1 flex items-center gap-3">
            <span className="text-[11px] text-ink-500">↵ to send · ⇧↵ for a new line</span>
            {error ? <span className="text-[11px] text-negative-500">{error}</span> : null}
          </div>
        </div>
      ) : (
        <p className="text-xs text-ink-500">Only the deal's owner can add messages.</p>
      )}
    </div>
  );
}

/** "1.2 MB" from a byte count. */
function fileSize(bytes: string | null): string {
  const n = Number(bytes ?? 0);
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

const PREVIEWABLE = /\.(pdf|txt|md|png|jpe?g|webp)$/i;
const THUMBNAILED = /\.(pdf|png|jpe?g|webp)$/i;

/** The stand-in for a file we can't draw: a type badge on a tinted card. */
function typeBadge(file: DealFile): { text: string; tone: string } {
  if (file.kind === 'link') {
    const url = file.url ?? '';
    if (url.includes('docs.google.com/spreadsheets')) return { text: 'Sheets', tone: 'bg-emerald-500/15 text-emerald-300' };
    if (url.includes('docs.google.com/document')) return { text: 'Docs', tone: 'bg-sky-500/15 text-sky-300' };
    return { text: 'Link', tone: 'bg-white/5 text-ink-300' };
  }
  const ext = (file.original_filename ?? '').split('.').pop()?.toLowerCase() ?? '';
  if (['xlsx', 'xlsm', 'xls', 'csv', 'ods', 'numbers'].includes(ext)) {
    return { text: ext.toUpperCase(), tone: 'bg-emerald-500/15 text-emerald-300' };
  }
  if (['docx', 'doc', 'odt'].includes(ext)) return { text: ext.toUpperCase(), tone: 'bg-sky-500/15 text-sky-300' };
  if (['pptx', 'ppt'].includes(ext)) return { text: ext.toUpperCase(), tone: 'bg-orange-500/15 text-orange-300' };
  return { text: ext.toUpperCase() || 'FILE', tone: 'bg-white/5 text-ink-300' };
}

/**
 * One document as a card: a picture of its first page (or a type badge when
 * there's nothing to draw), its name underneath; a click opens it in a new tab.
 */
function DocTile({
  href,
  thumb,
  badge,
  title,
  subtitle,
  tag,
  onRemove,
}: {
  href: string;
  thumb: string | null;
  badge: { text: string; tone: string };
  title: string;
  subtitle: string;
  tag?: string;
  onRemove?: () => void;
}) {
  const [broken, setBroken] = useState(false);
  return (
    <li className="group relative min-w-0">
      <a href={href} target="_blank" rel="noopener noreferrer" className="block" title={`Open ${title} in a new tab`}>
        <div className="relative aspect-[3/4] overflow-hidden rounded-lg border border-white/10 bg-ink-900/60 transition group-hover:border-white/30">
          {thumb && !broken ? (
            <img
              src={thumb}
              alt=""
              loading="lazy"
              onError={() => setBroken(true)}
              className="h-full w-full bg-white object-cover object-top"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <span className={`rounded-md px-2.5 py-1 text-sm font-semibold ${badge.tone}`}>{badge.text}</span>
            </div>
          )}
          {tag ? (
            <span className="pill absolute left-1.5 top-1.5 bg-ink-900/85 text-ink-100 shadow">{tag}</span>
          ) : null}
        </div>
        <span className="mt-1.5 block truncate text-xs text-ink-100 group-hover:underline">{title}</span>
        <span className="block truncate text-[11px] text-ink-500">{subtitle}</span>
      </a>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="absolute right-1.5 top-1.5 rounded bg-ink-900/85 px-1.5 py-0.5 text-[11px] text-ink-300 opacity-0 shadow transition-opacity hover:text-negative-500 focus:opacity-100 group-hover:opacity-100"
          aria-label={`Remove ${title}`}
        >
          Remove
        </button>
      ) : null}
    </li>
  );
}

/**
 * Everything else that belongs to the deal beside its main document: the
 * signed NDA, a second PDF, the cashflow model as a spreadsheet or a link to
 * the Google Sheet. Files go to the private bucket and open through a
 * short-lived link; the owner (or an admin) adds and removes them.
 */
function Files({ im }: { im: Im }) {
  const imId = im.id;
  const editable = useContext(EditableContext);
  const { ownerName } = useSession();
  const [files, setFiles] = useState<DealFile[] | null>(null);
  const [category, setCategory] = useState<DealFileCategory>('document');
  const [progress, setProgress] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    listDealFiles(imId)
      .then((rows) => {
        if (live) setFiles(rows);
      })
      .catch((e) => {
        if (live) setError((e as Error).message);
      });
    return () => {
      live = false;
    };
  }, [imId]);

  const upload = async (picked: File[]) => {
    if (picked.length === 0 || progress !== null) return;
    setError(null);
    setProgress(0);
    try {
      const added = await uploadDealFiles(imId, picked, category, setProgress);
      setFiles((prev) => [...(prev ?? []), ...added]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setProgress(null);
      if (picker.current) picker.current.value = '';
    }
  };

  const addLink = async () => {
    if (!linkUrl.trim()) return;
    setError(null);
    try {
      const isSheet = linkUrl.includes('docs.google.com/spreadsheets');
      const link = await addDealLink(imId, {
        url: linkUrl.trim(),
        label: linkLabel.trim() || undefined,
        // A sheet is a cashflow model unless a category was picked on purpose.
        category: isSheet && category === 'document' ? 'model' : category,
      });
      setFiles((prev) => [...(prev ?? []), link]);
      setLinkUrl('');
      setLinkLabel('');
      setLinkOpen(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async (file: DealFile) => {
    if (!window.confirm(`Remove “${file.label}” from this deal?`)) return;
    const previous = files;
    setFiles((prev) => (prev ?? []).filter((f) => f.id !== file.id));
    try {
      await deleteDealFile(imId, file.id);
    } catch (e) {
      setFiles(previous);
      setError((e as Error).message);
    }
  };

  const input =
    'rounded-lg border border-white/5 bg-ink-900/60 px-3 py-1.5 text-sm text-ink-100 placeholder:text-ink-500 focus:border-white/20 focus:outline-none';

  return (
    <div className="space-y-3">
      <ul className="grid grid-cols-4 gap-2 sm:grid-cols-8 lg:grid-cols-12">
        <DocTile
          href={imPreviewHref(imId)}
          thumb={imThumbnailHref(imId, im.gcs_object)}
          badge={{ text: 'PDF', tone: 'bg-white/5 text-ink-300' }}
          title={im.original_filename ?? (im.document_kind === 'teaser' ? 'Teaser' : 'Information Memorandum')}
          subtitle="Main document"
          tag={im.document_kind === 'teaser' ? 'Teaser' : 'IM'}
        />
        {im.teaser_gcs_object ? (
          <DocTile
            href={teaserPreviewHref(imId)}
            thumb={imThumbnailHref(imId, im.teaser_gcs_object, true)}
            badge={{ text: 'PDF', tone: 'bg-white/5 text-ink-300' }}
            title={im.teaser_original_filename ?? 'Teaser'}
            subtitle="Kept beside the IM"
            tag="Teaser"
          />
        ) : null}
        {(files ?? []).map((file) => (
          <DocTile
            key={file.id}
            href={
              file.kind === 'link'
                ? (file.url ?? '#')
                : dealFileHref(imId, file.id, PREVIEWABLE.test(file.original_filename ?? ''))
            }
            thumb={
              file.kind === 'file' && THUMBNAILED.test(file.original_filename ?? '')
                ? dealFileThumbnailHref(imId, file.id)
                : null
            }
            badge={typeBadge(file)}
            title={file.label}
            subtitle={`${file.kind === 'link' ? 'Link' : fileSize(file.size_bytes)} · ${
              ownerName(file.added_by) ?? 'Unknown'
            }, ${datetime(file.created_at)}`}
            tag={DEAL_FILE_CATEGORY_LABELS[file.category] ?? file.category}
            onRemove={editable ? () => void remove(file) : undefined}
          />
        ))}
      </ul>
      {files === null && !error ? <p className="text-xs text-ink-500">Loading the other files…</p> : null}
      {files === null && error ? <p className="text-xs text-negative-500">{error}</p> : null}

      {editable ? (
        <div className="space-y-2 border-t border-white/5 pt-3">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              void upload(Array.from(e.dataTransfer.files));
            }}
            className={`flex flex-wrap items-center gap-2 rounded-lg border border-dashed p-3 text-sm ${
              dragging ? 'border-accent-500 bg-accent-500/10' : 'border-white/10'
            }`}
          >
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as DealFileCategory)}
              className={input}
              aria-label="What the files are"
            >
              {DEAL_FILE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {DEAL_FILE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => picker.current?.click()}
              disabled={progress !== null}
              className="rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-ink-100 hover:bg-white/20 disabled:opacity-40"
            >
              {progress !== null ? `Uploading… ${Math.round(progress * 100)}%` : 'Add files'}
            </button>
            <button
              type="button"
              onClick={() => setLinkOpen((v) => !v)}
              className="rounded-md bg-white/5 px-3 py-1.5 text-xs font-medium text-ink-300 hover:bg-white/10"
            >
              Add link
            </button>
            <span className="text-[11px] text-ink-500">
              or drop files here · PDF, Excel, Word, CSV, images · 25 MB each
            </span>
            <input
              ref={picker}
              type="file"
              multiple
              accept={DEAL_FILE_ACCEPT}
              className="hidden"
              onChange={(e) => void upload(Array.from(e.target.files ?? []))}
            />
          </div>
          {linkOpen ? (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void addLink();
              }}
            >
              <input
                type="url"
                required
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/…"
                className={`min-w-0 flex-[2] ${input}`}
                autoFocus
              />
              <input
                type="text"
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
                placeholder="Name (optional)"
                className={`min-w-0 flex-1 ${input}`}
              />
              <button
                type="submit"
                disabled={!linkUrl.trim()}
                className="rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-ink-100 hover:bg-white/20 disabled:opacity-40"
              >
                Save link
              </button>
            </form>
          ) : null}
          {error && files !== null ? <p className="text-[11px] text-negative-500">{error}</p> : null}
        </div>
      ) : (
        <p className="text-xs text-ink-500">Only the deal's owner can add files.</p>
      )}
    </div>
  );
}

/**
 * The deal's stage as a progress bar: the pipeline left to right, then the
 * two ways out. One click moves the deal; moving it to "Want to see IM" or
 * "Want to talk" is what releases the agent to email about it.
 */
function StageStepper({
  stage,
  disabled,
  onPick,
}: {
  stage: Stage;
  disabled: boolean;
  onPick: (stage: Stage) => void;
}) {
  const pipeline = STAGES.filter((s) => !REJECTION_STAGES.includes(s));
  const at = pipeline.indexOf(stage);
  return (
    <div className="card flex flex-col gap-2 p-2 lg:flex-row lg:items-center">
      <div role="group" aria-label="Stage" className="grid flex-1 grid-cols-3 gap-1 sm:grid-cols-6">
        {pipeline.map((s, i) => {
          const current = s === stage;
          const done = at !== -1 && i < at;
          return (
            <button
              key={s}
              type="button"
              aria-pressed={current}
              disabled={disabled || current}
              onClick={() => onPick(s)}
              className={`relative rounded-lg px-2 py-2 text-left text-xs font-medium transition-colors ${
                current
                  ? 'bg-accent-500 text-white shadow-[0_6px_18px_-8px_rgb(108_116_245/0.9)]'
                  : done
                    ? 'bg-accent-500/15 text-accent-300 hover:bg-accent-500/25'
                    : 'bg-white/[0.03] text-ink-400 hover:bg-white/[0.07] hover:text-ink-100'
              } disabled:cursor-default ${disabled && !current ? 'opacity-60' : ''}`}
            >
              <span className="block text-[10px] font-semibold tabular-nums opacity-70">
                {done ? '✓' : i + 1}
              </span>
              {STAGE_LABELS[s]}
            </button>
          );
        })}
      </div>
      <div className="flex gap-1 lg:w-64">
        {REJECTION_STAGES.map((s) => {
          const current = s === stage;
          return (
            <button
              key={s}
              type="button"
              aria-pressed={current}
              disabled={disabled || current}
              onClick={() => onPick(s)}
              className={`flex-1 rounded-lg px-2 py-2 text-xs font-medium transition-colors ${
                current
                  ? 'bg-negative-500/20 text-negative-500 ring-1 ring-inset ring-negative-500/40'
                  : 'bg-white/[0.03] text-ink-500 hover:bg-negative-500/10 hover:text-negative-500'
              } disabled:cursor-default ${disabled && !current ? 'opacity-60' : ''}`}
            >
              {STAGE_LABELS[s]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Who has this deal, and the ownership actions open to the signed-in person:
 * claim it while it's unclaimed; release it if it's theirs; and, for an
 * admin, give it to anyone.
 */
function OwnerBar({ im, onChange }: { im: Im; onChange: (im: Im) => void }) {
  const { user, isAdmin, isMine, canEdit, ownerName, team } = useSession();
  const me = user.email.toLowerCase();
  const sendsFrom = team.find((m) => m.email === me)?.sends_from ?? me;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const owner = ownerName(im.owner_email);

  const run = async (action: () => Promise<Im>) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await action());
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        window.location.reload();
        return;
      }
      if (e instanceof AlreadyClaimedError && e.im) onChange(e.im);
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
      <OwnerBadge email={im.owner_email} name={owner} size="md" />
      {im.owner_email === null ? (
        <>
          <span className="text-ink-300">
            <span className="font-medium text-ink-100">Unclaimed.</span> Claim it to work this
            deal: you move it along, and the agent emails from{' '}
            {sendsFrom === me ? 'your address' : sendsFrom}.
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(() => claimIm(im.id))}
            className="rounded-md bg-positive-500/15 px-3 py-1.5 text-xs font-semibold text-positive-500 hover:bg-positive-500/25 disabled:opacity-50"
          >
            {busy ? 'Claiming…' : 'Claim this deal'}
          </button>
        </>
      ) : (
        <>
          <span className="text-ink-300">
            {isMine(im) ? (
              <span className="font-medium text-ink-100">Yours</span>
            ) : (
              <>
                Owned by <span className="font-medium text-ink-100">{owner}</span>
              </>
            )}
            {im.claimed_at ? <> since {datetime(im.claimed_at)}</> : null}
            {' · '}emails go out from {im.owner_email}
          </span>
          {canEdit(im) ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(() => releaseIm(im.id))}
              className="rounded-md bg-white/5 px-3 py-1.5 text-xs font-medium text-ink-100 hover:bg-white/10 disabled:opacity-50"
            >
              Release to the pool
            </button>
          ) : null}
        </>
      )}
      {isAdmin ? (
        <label className="ml-auto flex items-center gap-2 text-xs text-ink-300">
          Assign to
          <select
            value={im.owner_email ?? ''}
            disabled={busy}
            onChange={(e) => void run(() => assignIm(im.id, e.target.value || null))}
            className="rounded-md border border-white/10 bg-ink-900 px-2 py-1 text-xs text-ink-100"
          >
            <option value="">Nobody (unclaimed)</option>
            {team.map((m) => (
              <option key={m.email} value={m.email}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {error ? <span className="w-full text-xs text-negative-500">{error}</span> : null}
    </div>
  );
}

export function ImDetailView() {
  const { canEdit } = useSession();
  const [actionError, setActionError] = useState<string | null>(null);
  const { id = '' } = useParams();
  const [im, setIm] = useState<Im | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [previewDoc, setPreviewDoc] = useState<'main' | 'teaser'>('main');
  const [narration, setNarration] = useState<NarrationState>('idle');
  const [narrationUrl, setNarrationUrl] = useState<string | null>(null);
  const [narrationError, setNarrationError] = useState<string | null>(null);
  // Bumped by every reset so an in-flight fetch can tell it became stale (the
  // user navigated away or edited the deal) and must not surface its result.
  const narrationRunRef = useRef(0);

  // Drop the audio player (unmounting it stops playback) — used when the deal
  // changes or an edit makes the generated briefing stale.
  const resetNarration = useCallback(() => {
    narrationRunRef.current += 1;
    setNarration('idle');
    setNarrationUrl(null);
    setNarrationError(null);
  }, []);

  useEffect(() => resetNarration, [id, resetNarration]);

  const startNarration = useCallback(async () => {
    if (narration !== 'idle') return;
    setNarrationError(null);
    setNarration('loading');
    const run = narrationRunRef.current;
    try {
      const url = await fetchImNarration(id);
      if (narrationRunRef.current !== run) return; // went stale while fetching
      setNarrationUrl(url);
      setNarration('ready');
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        window.location.reload();
        return;
      }
      if (narrationRunRef.current !== run) return;
      resetNarration();
      setNarrationError(e instanceof Error ? e.message : String(e));
    }
  }, [id, narration, resetNarration]);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    getIm(id)
      .then((row) => {
        if (cancelled) return;
        setIm(row);
        setLoadState('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof AuthExpiredError) {
          window.location.reload();
          return;
        }
        if (e instanceof ImNotFoundError) {
          setLoadState('notfound');
          return;
        }
        setLoadError(e instanceof Error ? e.message : String(e));
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const save = useCallback(
    async (field: keyof PatchImFields, value: string | number | boolean | null) => {
      setSavingKeys((s) => new Set(s).add(field));
      try {
        const updated = await patchIm(id, { [field]: value } as PatchImFields);
        setIm(updated);
        // The cached audio briefing narrates the old values — drop it.
        resetNarration();
      } catch (e) {
        if (e instanceof AuthExpiredError) {
          window.location.reload();
          return;
        }
        if (e instanceof NotAllowedError) setActionError(e.message);
        // Surface inline by reloading the row to its server truth.
        try {
          setIm(await getIm(id));
        } catch {
          /* ignore */
        }
      } finally {
        setSavingKeys((s) => {
          const next = new Set(s);
          next.delete(field);
          return next;
        });
      }
    },
    [id, resetNarration],
  );
  const saving = (field: string) => savingKeys.has(field);

  if (loadState === 'loading') {
    return <div className="card p-8 text-center text-sm text-ink-300">Loading deal…</div>;
  }
  if (loadState === 'notfound') {
    return (
      <div className="card space-y-3 p-8 text-center">
        <div className="text-sm text-ink-100">That deal doesn’t exist.</div>
        <Link to="/ims" className="text-sm font-medium text-positive-500 hover:underline">
          ← Back to Deals
        </Link>
      </div>
    );
  }
  if (loadState === 'error' || !im) {
    return (
      <div className="card space-y-3 p-8 text-center">
        <div className="text-sm text-negative-500">Couldn’t load this deal.</div>
        {loadError ? <div className="text-xs text-ink-300">{loadError}</div> : null}
        <Link to="/ims" className="text-sm font-medium text-positive-500 hover:underline">
          ← Back to Deals
        </Link>
      </div>
    );
  }

  const detail = (im.rating_detail ?? {}) as RatingDetail;
  const title = im.name ?? im.original_filename ?? `IM #${im.opportunity_number}`;
  const editable = canEdit(im);

  return (
    <EditableContext.Provider value={editable}>
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link to="/ims" className="text-xs font-medium text-ink-300 hover:text-ink-100">
            ← Deals
          </Link>
          <h1 className="mt-1 truncate text-2xl font-semibold tracking-tight">{title}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-300">
            <span className="tabular-nums">Opportunity #{im.opportunity_number}</span>
            <span className="pill bg-white/5 text-ink-300">{im.source}</span>
            <button
              type="button"
              disabled={saving('still_in_running') || !editable}
              aria-pressed={im.still_in_running}
              onClick={() => save('still_in_running', !im.still_in_running)}
              title={editable ? 'Click to toggle' : undefined}
              className={`pill disabled:opacity-50 ${
                im.still_in_running
                  ? 'bg-positive-500/15 text-positive-400'
                  : 'bg-negative-500/15 text-negative-500'
              }`}
            >
              {im.still_in_running ? 'Still in running' : 'Not in running'}
            </button>
            {im.initial_rating !== null ? (
              <span className="pill bg-white/5 text-ink-100">Rated {im.initial_rating}/10</span>
            ) : im.rate_error ? (
              <span className="pill bg-negative-500/15 text-negative-500">Rating failed</span>
            ) : (
              <span className="pill bg-white/5 text-ink-300">Rating…</span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {narration === 'ready' && narrationUrl ? (
            <audio
              controls
              autoPlay
              src={narrationUrl}
              className="h-8"
              onError={() => {
                resetNarration();
                setNarrationError('Couldn’t play the audio briefing — press Listen to regenerate it.');
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => void startNarration()}
              disabled={narration === 'loading'}
              title="An AI voice reads the deal out to you"
              className="btn-primary"
            >
              {narration === 'idle' ? '▶ Listen' : 'Preparing audio…'}
            </button>
          )}
          <a
            href={imPreviewHref(im.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
          >
            Open PDF
          </a>
          <a
            href={imDownloadHref(im.id)}
            download
            className="btn-ghost"
          >
            Download
          </a>
          {im.teaser_gcs_object ? (
            <a
              href={teaserPreviewHref(im.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
            >
              Open teaser
            </a>
          ) : null}
        </div>
      </div>

      {narrationError ? (
        <div className="text-xs text-negative-500">{narrationError}</div>
      ) : null}

      <StageStepper
        stage={im.stage}
        disabled={saving('stage') || !editable}
        onPick={(stage) => save('stage', stage)}
      />

      <OwnerBar
        im={im}
        onChange={(updated) => {
          setIm(updated);
          setActionError(null);
        }}
      />
      {actionError ? <div className="text-xs text-negative-500">{actionError}</div> : null}

      <Card title="Product / Service">
        {detail.productSummary ? (
          <p className="text-sm leading-relaxed text-ink-100">{detail.productSummary}</p>
        ) : (
          <p className="text-sm text-ink-500">
            No summary yet — this IM was rated before product summaries were captured. Re-rate it
            to generate one.
          </p>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Rating">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-semibold tabular-nums">
              {im.initial_rating ?? '—'}
            </span>
            <span className="text-sm text-ink-300">/ 10</span>
          </div>
          {detail.verdict ? (
            <p className="mt-1 text-sm text-ink-100">{detail.verdict}</p>
          ) : null}
          {Array.isArray(detail.dimensions) && detail.dimensions.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {detail.dimensions.map((d) => (
                <li key={d.name} className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-ink-100">{d.name}</span>
                    <span className="tabular-nums text-ink-300">{d.score}/10</span>
                  </div>
                  {d.rationale ? (
                    <p className="text-xs text-ink-400">{d.rationale}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {detail.notes ? (
            <p className="mt-3 whitespace-pre-wrap text-xs text-ink-300">{detail.notes}</p>
          ) : null}
          <div className="mt-3 border-t border-white/5 pt-2 text-xs text-ink-500">
            {im.rated_at ? `Rated ${new Date(im.rated_at).toLocaleString('en-GB')}` : 'Not yet rated'}
            {im.rate_error ? (
              <span className="text-negative-500"> · error: {im.rate_error}</span>
            ) : null}
          </div>
          <EditField
            label="Manual rating override"
            kind="number"
            raw={im.initial_rating}
            display={plain(im.initial_rating)}
            saving={saving('initial_rating')}
            onSave={(v) => save('initial_rating', v)}
          />
        </Card>

        <Card title="Financials">
          <div className="grid grid-cols-2 gap-x-4">
            <EditField label="Revenue" kind="number" raw={im.revenue} display={money(im.revenue)} saving={saving('revenue')} onSave={(v) => save('revenue', v)} />
            <EditField label="EBITDA" kind="number" raw={im.ebitda} display={money(im.ebitda)} saving={saving('ebitda')} onSave={(v) => save('ebitda', v)} />
            <div className="py-2">
              <span className="text-xs font-medium uppercase tracking-wide text-ink-500">EBITDA margin</span>
              <div className="px-1 py-0.5 text-sm text-ink-100">{margin(im.revenue, im.ebitda)}</div>
            </div>
            <EditField label="Clients" kind="number" raw={im.num_clients === null ? null : String(im.num_clients)} display={plain(im.num_clients)} saving={saving('num_clients')} onSave={(v) => save('num_clients', v)} />
            <EditField label="Asking for" kind="number" raw={im.asking_for} display={money(im.asking_for)} saving={saving('asking_for')} onSave={(v) => save('asking_for', v)} />
            <EditField label="Offered" kind="number" raw={im.offered} display={money(im.offered)} saving={saving('offered')} onSave={(v) => save('offered', v)} />
            <div className="col-span-2">
              <EditField label="Seller(s)" kind="text" raw={im.sellers} display={plain(im.sellers)} saving={saving('sellers')} onSave={(v) => save('sellers', v)} />
            </div>
          </div>
        </Card>

        <Card title="Company">
          <EditField label="Broker reference" kind="text" raw={im.broker_reference} display={plain(im.broker_reference)} saving={saving('broker_reference')} onSave={(v) => save('broker_reference', v)} />
          <EditField label="Name" kind="text" raw={im.name} display={im.name ?? <span className="text-ink-500">{im.original_filename ?? '—'}</span>} saving={saving('name')} onSave={(v) => save('name', v)} />
          <EditField label="Founder" kind="text" raw={im.founder} display={plain(im.founder)} saving={saving('founder')} onSave={(v) => save('founder', v)} />
          <EditField label="Tech stack" kind="text" raw={im.tech_stack} display={plain(im.tech_stack)} saving={saving('tech_stack')} onSave={(v) => save('tech_stack', v)} multiline />
          <EditField label="Knightsbridge contact" kind="text" raw={im.knightsbridge_contact} display={plain(im.knightsbridge_contact)} saving={saving('knightsbridge_contact')} onSave={(v) => save('knightsbridge_contact', v)} />
          {/* Who the agent writes to. Empty means it will not send at all. */}
          <EditField label="Counterparty email" kind="text" raw={im.counterparty_email} display={plain(im.counterparty_email)} saving={saving('counterparty_email')} onSave={(v) => save('counterparty_email', v)} />
          <EditField label="Companies House no." kind="text" raw={im.company_number} display={plain(im.company_number)} saving={saving('company_number')} onSave={(v) => save('company_number', v)} />
        </Card>

        <Card title="Workflow">
          {im.call_scheduled_at ? (
            <div className="py-2 text-xs text-ink-300">
              Call booked for {datetime(im.call_scheduled_at)}
            </div>
          ) : null}
          <ToggleRow label="Still in running" value={im.still_in_running} onToggle={(v) => save('still_in_running', v)} saving={saving('still_in_running')} offTone="negative" />
          <ToggleRow label="First call requested" value={im.first_call_requested} onToggle={(v) => save('first_call_requested', v)} saving={saving('first_call_requested')} />
          <ToggleRow label="First call scheduled" value={im.first_call_scheduled} onToggle={(v) => save('first_call_scheduled', v)} saving={saving('first_call_scheduled')} />
          {im.rejected_at ? (
            <div className="py-2 text-xs text-ink-500">
              Rejected {new Date(im.rejected_at).toLocaleString('en-GB')}
            </div>
          ) : null}
          <EditField label="Outcome" kind="text" raw={im.outcome} display={plain(im.outcome)} saving={saving('outcome')} onSave={(v) => save('outcome', v)} />
          {/* The standing summary, overwritten on each edit — the dated series
              lives in its own card at the bottom of the page. */}
          <EditField label="Summary note" kind="text" raw={im.notes} display={im.notes ?? '—'} saving={saving('notes')} onSave={(v) => save('notes', v)} multiline />
        </Card>
      </div>

      <Card title="Email agent">
        {im.agent_paused_reason ? (
          <div className="mb-3 rounded-lg border border-warn-500/30 bg-warn-500/10 p-3 text-sm text-warn-500">
            <p className="font-medium">
              The agent needs {editable && im.owner_email ? 'you' : 'the owner'}
            </p>
            <p className="mt-1 text-ink-100">{im.agent_paused_reason}</p>
            <button
              type="button"
              disabled={saving('agent_paused_reason') || !editable}
              onClick={() => save('agent_paused_reason', null)}
              className="mt-2 rounded-md bg-white/5 px-3 py-1.5 text-xs font-medium text-ink-100 hover:bg-white/10 disabled:opacity-50"
            >
              Clear and let it carry on
            </button>
          </div>
        ) : null}
        <ToggleRow
          label="Agent may email about this deal"
          value={im.agent_enabled}
          onToggle={(v) => save('agent_enabled', v)}
          saving={saving('agent_enabled')}
          offTone="negative"
        />
        <p className="pt-2 text-xs text-ink-500">
          The agent only writes about deals at “Want to see IM”, “Want to talk” or “Call
          scheduled”, only once someone has claimed them, and from the owner’s mailbox (or Andi’s, for owners set to send from it). It
          never sends more than one message per deal per run.
        </p>
        <div className="mt-3 border-t border-white/5 pt-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-300">
            Correspondence
          </h3>
          <Correspondence imId={im.id} />
        </div>
      </Card>

      <Card title="Notes">
        <Notes imId={im.id} />
      </Card>

      <Card title="Documents">
        <Files im={im} />
      </Card>

      <Card title={im.document_kind === 'teaser' ? 'Teaser' : 'Information Memorandum'}>
        {im.teaser_gcs_object ? (
          <div className="mb-3 flex gap-1 text-xs">
            {(['main', 'teaser'] as const).map((doc) => (
              <button
                key={doc}
                type="button"
                onClick={() => setPreviewDoc(doc)}
                className={`rounded-md px-3 py-1.5 font-medium ${
                  previewDoc === doc
                    ? 'bg-white/15 text-ink-100'
                    : 'bg-white/5 text-ink-500 hover:bg-white/10'
                }`}
              >
                {doc === 'main' ? 'IM' : 'Teaser'}
              </button>
            ))}
          </div>
        ) : null}
        <iframe
          key={previewDoc}
          title={`Preview of ${title}`}
          src={
            previewDoc === 'teaser' && im.teaser_gcs_object
              ? teaserPreviewHref(im.id)
              : imPreviewHref(im.id)
          }
          className="h-[85vh] w-full rounded bg-white"
        />
      </Card>
    </section>
    </EditableContext.Provider>
  );
}
